/**
 * Theme + UI prefs · types re-exported from `@shared/schemas/ui-prefs.schema`
 * (ADR-002). Runtime helpers (label maps · cycle constants · `nextTheme`) stay
 * here as frontend-only concerns.
 */
import type { ThemeAttr, ThemeName } from '@shared/schemas/ui-prefs.schema';

export type {
  ThemeAttr,
  ThemeName,
  Density,
  Decoration,
  UiPrefs,
  FontScale,
} from '@shared/schemas/ui-prefs.schema';

export const THEME_TO_ATTR: Record<ThemeName, ThemeAttr> = {
  warmsunset: 'light',
  coolmist: 'mint',
  deepstudio: 'dark',
  indigoday: 'indigo-light',
  indigonight: 'indigo-dark',
};

export const ATTR_TO_THEME: Record<ThemeAttr, ThemeName> = {
  light: 'warmsunset',
  mint: 'coolmist',
  dark: 'deepstudio',
  'indigo-light': 'indigoday',
  'indigo-dark': 'indigonight',
};

export const THEME_LABEL: Record<ThemeName, string> = {
  warmsunset: 'Тёплая бумага',
  // Internal name `coolmist` (attr `mint`) is legacy from pre-W1.5 era.
  // slot_dark_theme DT.8 + slot_cyanotype_theme (2026-05-18 -> 2026-05-31)
  // iterations:
  //   round 5 (Ice & Rowan · холодная бумага)    -> label «Лёд и рябина» — current (3 JUL 2026)
  //   round 4 (Cyanotype · drafter's blueprint)  -> label «Цианотип» — retired
  //   round 3 (G3 · Coral · terracotta)          -> label «Коралл»   — retired
  //   round 2 (G2 · Muted Punk · -30% sat)       -> label same       — too pink
  //   round 1 (G  · Hot Pink Punk Zine)          -> label «Панк-зин» — too sweet
  // Round 4 character = warm paper + blueprint navy + ochre stamp + iron-oxide
  // red (bugs/errors only · matches --plum rare-role grammar). Internal type
  // name `coolmist` stays для shared-schema stability (ADR-002). Full rename
  // = future cleanup slot. Full WCAG calc + palette rationale в
  // COORDINATION/slot_cyanotype_theme.log.md.
  coolmist: 'Лёд и рябина',
  deepstudio: 'Глубина',
  // Тёмные темы эволюционировали (3 JUL 2026 · Genie): «Чернила · бирюза» →
  // «Глубина» (вода · бирюза · коралл · янтарь), «Индиго · ночь» → «Обсерватория»
  // (ночное небо · виолет · коралл · звёздное золото). Внутренние имена
  // deepstudio/indigonight и attr dark/indigo-dark НЕ меняются (редеплой edge
  // не нужен). Значения палитр — в globals.css [data-theme="dark"/"indigo-dark"].
  indigoday: 'Лён · индиго',
  indigonight: 'Обсерватория',
};

/** Cycle order — warm -> mint -> dark -> indigo light -> indigo night -> warm. */
export const THEME_CYCLE: ThemeName[] = ['warmsunset', 'coolmist', 'deepstudio', 'indigoday', 'indigonight'];

export const nextTheme = (current: ThemeName): ThemeName => {
  const idx = THEME_CYCLE.indexOf(current);
  return THEME_CYCLE[(idx + 1) % THEME_CYCLE.length] ?? 'warmsunset';
};

/* ── Theme-picker metadata · slot_theme_inkteal_indigo (29 JUN 2026) ──
   Общий источник для поповера (сайдбар/топбар) и сетки в Профиле. Swatch-цвета —
   зафиксированные hex из соответствующих [data-theme] блоков в globals.css
   (показывают, как тема выглядит, независимо от текущей активной). */
export interface ThemeMeta {
  label: string;
  group: 'light' | 'dark';
  swatch: { bg: string; surface: string; accent: string };
}

export const THEME_META: Record<ThemeName, ThemeMeta> = {
  warmsunset:  { label: 'Тёплая бумага',   group: 'light', swatch: { bg: '#FBEFD9', surface: '#FFFAEE', accent: '#F26B4A' } },
  coolmist:    { label: 'Лёд и рябина',      group: 'light', swatch: { bg: '#E3EDF2', surface: '#F6FAFB', accent: '#2C67A8' } }, /* round 5 «Лёд и рябина» (3 JUL 2026) */
  indigoday:   { label: 'Лён · индиго',      group: 'light', swatch: { bg: '#F1E8D6', surface: '#FAF4E6', accent: '#4652BD' } }, /* v3 «Лён и краситель» (3 JUL 2026) */
  deepstudio:  { label: 'Глубина',           group: 'dark',  swatch: { bg: '#0D1A1E', surface: '#14252B', accent: '#36C9B4' } },
  indigonight: { label: 'Обсерватория',      group: 'dark',  swatch: { bg: '#151830', surface: '#1D2140', accent: '#8C7DF2' } },
};

export interface ThemeGroup {
  id: 'light' | 'dark';
  label: string;
  themes: ThemeName[];
}

export const THEME_GROUPS: ReadonlyArray<ThemeGroup> = [
  { id: 'light', label: 'Светлые', themes: ['warmsunset', 'coolmist', 'indigoday'] },
  { id: 'dark', label: 'Тёмные', themes: ['deepstudio', 'indigonight'] },
];

// ============================================================
// Background pattern variants — Riso [data-bg] system.
// Per slot_riso_final_ds Phase A1.2 (15 MAY 2026): 5 variants match
// polished_prototype_riso/styles.css html[data-bg=...] selectors.
// Default 'paper' matches index.html static attribute + app.jsx TWEAK_DEFAULTS.
// Frontend-local state (NOT persisted to UiPrefs shared schema) per Q-4
// Tweaks dev-only philosophy.
//
// slot_cyanotype_theme (2026-05-31): added 'blueprint' variant. Cyanotype
// theme only · activation via dev-tools console
// `document.documentElement.setAttribute('data-bg','blueprint')`
// (no UI · TweaksPanel retired 17 MAY 2026 per H.4-EXT2.3 ·
// Genie pick C-B1.a 2026-05-31). On other themes the attribute is a
// silent no-op (no CSS rule fires).
// ============================================================
export type BgPattern = 'paper' | 'grid' | 'dots' | 'halftone' | 'plain' | 'blueprint';

export const BG_PATTERN_DEFAULT: BgPattern = 'paper';

export const BG_PATTERN_LABEL: Record<BgPattern, string> = {
  paper: 'Бумага',
  grid: 'Сетка',
  dots: 'Точки',
  halftone: 'Хафтон',
  plain: 'Плоский',
  blueprint: 'Цианотип-чертёж',
};

export const BG_PATTERNS: ReadonlyArray<BgPattern> = ['paper', 'grid', 'dots', 'halftone', 'plain', 'blueprint'];


// ============================================================
// Text-size control A / A+ / A++ · MOBILE_READABILITY (2026-06-06).
// base = current sizes · lg = x1.15 · xl = x1.3 (--font-scale CSS var).
// ============================================================
import type { FontScale } from '@shared/schemas/ui-prefs.schema';

export const FONT_SCALE_DEFAULT: FontScale = 'base';

export const FONT_SCALE_CYCLE: FontScale[] = ['base', 'lg', 'xl'];

export const FONT_SCALE_LABEL: Record<FontScale, string> = {
  base: 'A',
  lg: 'A+',
  xl: 'A++',
};

export const nextFontScale = (current: FontScale): FontScale => {
  const idx = FONT_SCALE_CYCLE.indexOf(current);
  return FONT_SCALE_CYCLE[(idx + 1) % FONT_SCALE_CYCLE.length] ?? 'base';
};
